import { Box, HStack, VStack, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { ArrowSquareUpRightIcon, CheckCircleBrokenIcon, Button, Text, HelpCircleIcon } from '@pimm/base';
import { EmpLearningPathStatusEnum, LearningPathStatDto } from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { getLearningPathMetaStats } from '../_helper';
import { LearningPathBadge } from './learning-path-badge';
import { PinSetupModalType } from '@app/features/store-core/types';
import { ActivityIcon } from '../icons';
import { PopoverTooltipCompleted } from './popover-tooltip-completed';

export type LearningPathCardProps = {
  isPinSet?: boolean;
  learningPath: LearningPathStatDto;
  onOpenModal: (_name: PinSetupModalType, url?: string) => () => void;
};

export const LearningPathCard = ({ isPinSet = false, learningPath, ...props }: LearningPathCardProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const siteTime = useSiteTime();

  const stats = getLearningPathMetaStats(learningPath.learningStatus);

  return (
    <Box
      rounded="xl"
      pt={3}
      pb={4}
      px={4}
      borderColor="gray.200"
      borderWidth={1}
      bg="gray.25"
      style={{
        shadowColor: colors.gray[500],
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.4,
        shadowRadius: 1,
      }}
    >
      <HStack alignItems="center" justifyContent="space-between">
        <Text size="xl" fontWeight={600} color="black" lineHeight="xs">
          {learningPath.learningPath ?? ''}
        </Text>

        <Button
          variant="unstyled"
          onPress={props.onOpenModal(isPinSet ? 'validate-pin' : 'pin-setup', learningPath?.learningPathUrl)}
          endIcon={<ArrowSquareUpRightIcon size="sm" color="black" />}
        >
          {t(stats.actionText)}
        </Button>
      </HStack>

      <VStack space={3}>
        <Box alignItems="flex-start">
          <LearningPathBadge status={learningPath.learningStatus ?? EmpLearningPathStatusEnum.Pending} />
        </Box>

        {learningPath.learningStatus === EmpLearningPathStatusEnum.Complete ? (
          <HStack space={2} alignItems="center">
            <CheckCircleBrokenIcon size="sm" color={colors.gray[700]} />
            <Text size="md" fontWeight={500} color="gray.700" numberOfLines={1} ellipsizeMode="tail">
              {t('app_insights:completed_on')}: 
              {moment
                .utc(learningPath.completedDateTimeUtc)
                .utcOffset(siteTime.utcOffsetInMinutes ?? 0)
                .format('MM/DD/YYYY h:mm A')}
            </Text>
            <PopoverTooltipCompleted
              isManual={learningPath.manualCompleted!}
              placement="right"
              trigger={triggerProps => (
                <Button variant="unstyled" endIcon={<HelpCircleIcon size="sm" color="gray.700" />} minH={26} px={2} {...triggerProps}>
                  {learningPath?.manualCompleted ? t('app_insights:manual') : t('app_insights:auto')}
                </Button>
              )}
            />
          </HStack>
        ) : learningPath.learningStatus === EmpLearningPathStatusEnum.Started ? (
          <HStack space={2}>
            <ActivityIcon size="sm" color={colors.gray[600]} />
            <Text size="md" fontWeight={500} color="gray.700" numberOfLines={1} ellipsizeMode="tail">
              {learningPath.lastActivityDaysAgo
                ?.toLowerCase()
                .replace('no activity', t('app_insights:no_activity'))
                .replace('days ago', t('app_insights:days_ago'))}
            </Text>
          </HStack>
        ) : null}
      </VStack>
    </Box>
  );
};
